import {
  EDGE_VISIBILITY_PADDING_PX,
  MIN_THUMB_WIDTH_PX,
  PREVIEW_CARD_SELECTOR_ATTR,
} from "./constants";
import { ScrollbarMetrics } from "./types";

export const getScrollbarMetrics = (
  scrollElement: HTMLDivElement,
  trackWidth: number
): ScrollbarMetrics => {
  const maxScroll = scrollElement.scrollWidth - scrollElement.clientWidth;
  if (maxScroll <= 32 || trackWidth <= 0) {
    return { visible: false, thumbWidth: 0, thumbOffset: 0, scrollPercentage: 0 };
  }

  const thumbWidth = Math.max(
    (scrollElement.clientWidth / scrollElement.scrollWidth) * trackWidth,
    MIN_THUMB_WIDTH_PX
  );
  const thumbTravel = Math.max(trackWidth - thumbWidth, 0);
  const scrollRatio = scrollElement.scrollLeft / maxScroll;
  const thumbOffset = scrollRatio * thumbTravel;

  return {
    visible: true,
    thumbWidth,
    thumbOffset,
    scrollPercentage: Math.round(scrollRatio * 100),
  };
};

export const getThumbTravel = (trackWidth: number, thumbWidth: number): number =>
  Math.max(trackWidth - thumbWidth, 0);

export const isCardFullyVisible = (card: HTMLElement, container: HTMLElement): boolean => {
  const cardRect = card.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return (
    cardRect.left >= containerRect.left + EDGE_VISIBILITY_PADDING_PX &&
    cardRect.right <= containerRect.right - EDGE_VISIBILITY_PADDING_PX
  );
};

export const getScrollLeftToCenterCard = (
  card: HTMLElement,
  scrollElement: HTMLDivElement
): number => {
  const maxScroll = Math.max(0, scrollElement.scrollWidth - scrollElement.clientWidth);
  const containerRect = scrollElement.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const cardCenterInScrollContent =
    cardRect.left - containerRect.left + scrollElement.scrollLeft + cardRect.width / 2;
  const targetScrollLeft = cardCenterInScrollContent - scrollElement.clientWidth / 2;

  return Math.max(0, Math.min(maxScroll, targetScrollLeft));
};

export const scrollActiveCardIntoView = (scrollElement: HTMLDivElement, activeId: string): void => {
  const activeCard = scrollElement.querySelector<HTMLElement>(
    `[${PREVIEW_CARD_SELECTOR_ATTR}="${activeId}"]`
  );

  if (!activeCard || isCardFullyVisible(activeCard, scrollElement)) {
    return;
  }

  const left = getScrollLeftToCenterCard(activeCard, scrollElement);

  if (typeof scrollElement.scrollTo === "function") {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    scrollElement.scrollTo({
      left,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
    return;
  }

  scrollElement.scrollLeft = left;
};

export const getScrollLeftFromTrackClick = (
  scrollElement: HTMLDivElement,
  trackWidth: number,
  clickX: number
): number => {
  const maxScroll = scrollElement.scrollWidth - scrollElement.clientWidth;
  if (maxScroll <= 0) {
    return 0;
  }

  const { thumbWidth } = getScrollbarMetrics(scrollElement, trackWidth);
  const thumbTravel = getThumbTravel(trackWidth, thumbWidth);
  const targetRatio =
    thumbTravel > 0 ? Math.max(0, Math.min(1, (clickX - thumbWidth / 2) / thumbTravel)) : 0;

  return targetRatio * maxScroll;
};

export const getScrollLeftFromThumbDrag = (
  scrollElement: HTMLDivElement,
  trackWidth: number,
  startScrollLeft: number,
  deltaX: number
): number => {
  const maxScroll = scrollElement.scrollWidth - scrollElement.clientWidth;
  if (maxScroll <= 0) {
    return startScrollLeft;
  }

  const { thumbWidth } = getScrollbarMetrics(scrollElement, trackWidth);
  const thumbTravel = getThumbTravel(trackWidth, thumbWidth);

  if (thumbTravel <= 0) {
    return startScrollLeft;
  }

  return startScrollLeft + (deltaX / thumbTravel) * maxScroll;
};
